import { createContext, useEffect, PropsWithChildren } from 'react';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import CurrentUser from 'src/types/CurrentUser';
import useAuth from 'src/api/auth/useAuth';
import useLogOut from 'src/api/auth/useLogOut';
import { authzClient } from 'src/context/authz';

export interface AuthContextData {
  isFetchingAuthInitialStatus: boolean;
  currentUser?: CurrentUser;
  errorLoadingUser?: Error | unknown;
  refetchUser: (options?: RefetchOptions) => Promise<QueryObserverResult>;
  isRefetchingUser: boolean;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const posthog = usePostHog();
  const {
    isFetchingAuthInitialStatus,
    currentUser,
    error: errorLoadingUser,
    isRefetching: isRefetchingUser,
    refetchUser,
  } = useAuth();

  const { mutateAsync: logOutApi } = useLogOut();

  // Identify user in PostHog when currentUser is loaded
  useEffect(() => {
    if (currentUser && posthog) {
      // Set person properties for feature flag evaluation before reloading flags
      // https://posthog.com/docs/libraries/js#overriding-server-properties
      posthog.setPersonPropertiesForFlags({
        organizationId: currentUser.organizationId,
      });

      posthog.identify(currentUser.id, {
        email: currentUser.emails[0],
        name: currentUser.name,
        organizationId: currentUser.organizationId,
      });

      // Reload feature flags with the new person properties
      posthog.reloadFeatureFlags();
    }
  }, [currentUser, posthog]);

  const logOut = async () => {
    if (posthog) {
      posthog.reset();
    }
    // Reset authorization cache to prevent permission leakage between users
    authzClient.reset();
    await logOutApi();
    await refetchUser();
    window.location.assign('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        isFetchingAuthInitialStatus,
        currentUser,
        errorLoadingUser,
        refetchUser,
        isRefetchingUser,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
